import { useState } from 'react'
import { generatePath } from 'react-router-dom'
import { State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientDuplicateDto } from 'api/Clients/Types/ClientDuplicateDto'
import { Link } from 'components/Link'
import { pageableSettings } from 'store/grid/grid.slice'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { getPaginationInitialState } from 'utils/paginationInitialState'

const DetailComponent = (props: GridDetailRowProps) => {
  const data: string[] = props.dataItem.matchTypes
  const dataMatchField = (data || []).reduce((acc: { match: string }[], row: string) => {
    acc.push({ match: row })
    return acc
  }, [])
  if (dataMatchField.length) {
    return (
      <Grid data={dataMatchField} resizable>
        <Column className='table-columns__content' field='match' title='Match Type' />
      </Grid>
    )
  }
  return (
    <div style={{ height: '50px', width: '100%' }}>
      <div style={{ position: 'absolute', width: '100%' }}>
        <div className='k-loading-image' />
      </div>
    </div>
  )
}

const createDataState = (state: State, data: ClientDuplicateDto[]) => ({
  result: data.slice(state.skip, (state.take as number) + (state.skip as number)),
  dataState: state,
})

const ClientAreaDuplicateGrid = ({
  duplicates,
}: {
  duplicates: ClientDuplicateDto[]
}): JSX.Element => {
  const [dataState, setDataState] = useState<{ result: ClientDuplicateDto[]; dataState: State }>(
    createDataState(getPaginationInitialState(), duplicates || [])
  )

  const pageChange = (event: GridPageChangeEvent) => {
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    setDataState(createDataState({ take: event.page.take, skip: event.page.skip }, duplicates))
  }

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(createDataState(event.dataState, duplicates as ClientDuplicateDto[]))
  }

  const expandChange = (event: GridExpandChangeEvent) => {
    event.dataItem[event.target.props.expandField as string] = event.value
    setDataState(createDataState(dataState.dataState, duplicates))
  }

  return (
    <Grid
      style={{ zIndex: 0, margin: '2rem' }}
      resizable
      take={dataState.dataState.take}
      skip={dataState.dataState.skip}
      onDataStateChange={dataStateChange}
      total={duplicates.length}
      data={dataState.result}
      sortable
      pageable={pageableSettings}
      onPageChange={pageChange}
      detail={DetailComponent}
      expandField='expanded'
      onExpandChange={expandChange}
    >
      <Column className='table-columns__content' field='tickmillCompany.name' title='Entity' />
      <Column
        className='table-columns__content'
        cell={(props: GridCellProps) => (
          <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
            <Link
              to={{
                pathname: generatePath(links.recordsClients.detailsUrl as string, {
                  id: props.dataItem.client.id,
                  slug: 'details',
                }),
              }}
            >
              {props.dataItem.client.name}
            </Link>
          </td>
        )}
        title='Client'
      />
      <Column
        className='table-columns__date'
        cell={(props: GridCellProps) => <td>{formatLocalDateUTC(props.dataItem.birthday)}</td>}
        title='Birthday'
      />
      <Column className='table-columns__content' field='country.name' title='Country' />
      <Column className='table-columns__content' field='phoneNumber' title='Phone number' />
      <Column className='table-columns__content' field='type.name' title='Type' />
      <Column className='table-columns__content' field='status.name' title='Status' />
      <Column className='table-columns__content' field='statusReason' title='Status Reason' />
      <Column
        field='createdAt'
        title='Created'
        cell={(props: GridCellProps) => (
          <td className='table-columns__date'>
            {formatLocalDateUTC(props.dataItem.createdDate, true)}
          </td>
        )}
      />
    </Grid>
  )
}

export default ClientAreaDuplicateGrid
